import React, { useState, useEffect, useCallback } from 'react';
import { X, Sparkles, ArrowRight } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

/**
 * SeasonalBanner — dismissible announcement at the top of HomeScreen +
 * Screen_MemberHome.
 *
 * Layout (Lot 9 Phase 9A v2 — UX fix June 2026):
 *   - Sits in NORMAL DOCUMENT FLOW — no sticky/fixed/absolute positioning.
 *     Everything below (hero, video, content) is pushed down by the
 *     banner's natural height, never overlapped.
 *   - Mounted as the first child of the home screen content so it sits
 *     ABOVE the Sunday Service hero. Tapping any hero CTA stays
 *     unobstructed.
 *
 * Dismissal:
 *   - localStorage key `dismissed_banners` is an object
 *       { [bannerId]: dismissedAtMillis }
 *   - A dismissed banner stays hidden for 24 h, then reappears.
 *   - A DIFFERENT banner id always appears immediately, regardless of
 *     prior dismissals (Louis spec).
 *   - Stale entries (banners no longer returned by the server) are
 *     pruned opportunistically on each fetch — bounded growth even with
 *     years of usage.
 *
 * Visual (Lot 9 Phase 9A v2):
 *   - Lighter gold-tinted background (#FBF5EA) with a darker gold
 *     accent on the LEFT border — editorial, not intrusive.
 *   - No drop shadow — the banner is part of the page, not floating.
 *   - Smaller close button so it doesn't compete with the CTA.
 *   - Subtle bottom margin separates the banner from the hero below.
 *   - Title in PCC dark gold (#A87344) over the cream background;
 *     description in warm-gray for second-tier hierarchy.
 *
 * Refresh policy (updated June 2026 — bug: deleted banner persisted in
 * the app until the user manually reloaded):
 *   - No client-side cache. Every fetch hits /api/banners/active.
 *   - Refetch on mount, on `visibilitychange` becoming-visible, AND
 *     every 15 s while the document is visible. The interval is paused
 *     when the tab is backgrounded so we don't burn requests in the
 *     background. Matches the HeroCarousel pattern (15 s freshness).
 */

import { API_BASE as BACKEND_URL } from '../../utils/apiBase';
const DISMISSED_KEY = 'dismissed_banners';
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24 h
const REFRESH_INTERVAL_MS = 15 * 1000;       // Poll every 15 s while visible

function readDismissedMap() {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
  } catch (_) {
    return {};
  }
}

function writeDismissedMap(map) {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(map));
  } catch (_) {
    // Out of quota / disabled — silently ignore so we never break render.
  }
}

function isStillDismissed(map, bannerId, now = Date.now()) {
  const ts = map?.[bannerId];
  if (typeof ts !== 'number') return false;
  return (now - ts) < DISMISS_TTL_MS;
}

const SeasonalBanner = () => {
  const [banner, setBanner] = useState(null);
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);

  const fetchBanner = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/banners/active`, {
        credentials: 'include',
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const b = data?.banner;

      // Opportunistic prune: drop dismissal entries that are already
      // older than 24 h so localStorage stays small. We DO NOT prune
      // entries for "banners no longer on the server" here — we don't
      // have visibility into all server banner ids from /active. That
      // pruning falls out naturally from the TTL.
      const map = readDismissedMap();
      const now = Date.now();
      let changed = false;
      for (const [id, ts] of Object.entries(map)) {
        if (typeof ts !== 'number' || (now - ts) >= DISMISS_TTL_MS) {
          delete map[id];
          changed = true;
        }
      }
      if (changed) writeDismissedMap(map);

      if (!b) {
        setBanner(null);
        setVisible(false);
        return;
      }
      if (isStillDismissed(map, b.id, now)) {
        setBanner(null);
        setVisible(false);
        return;
      }
      setBanner(b);
      setVisible(true);
      // Trigger slide-in on next frame so transition fires
      requestAnimationFrame(() => setEntered(true));
    } catch (_) {
      // Network blip — never break the home screen
    }
  }, []);

  useEffect(() => {
    fetchBanner();
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchBanner();
    };
    document.addEventListener('visibilitychange', onVis);
    // 15s polling, but ONLY while the document is visible. A backgrounded
    // tab stops burning /api/banners/active calls; coming back to the tab
    // triggers an immediate refetch via visibilitychange.
    const pollId = setInterval(() => {
      if (document.visibilityState === 'visible') fetchBanner();
    }, REFRESH_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(pollId);
    };
  }, [fetchBanner]);

  const handleDismiss = () => {
    if (!banner) return;
    const map = readDismissedMap();
    map[banner.id] = Date.now();
    writeDismissedMap(map);
    setEntered(false);
    // Wait for slide-out, then unmount.
    setTimeout(() => setVisible(false), 220);
  };

  const handleCta = () => {
    if (!banner?.ctaUrl) return;
    if (banner.ctaUrl.startsWith('http')) {
      window.open(banner.ctaUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.location.assign(banner.ctaUrl);
    }
  };

  if (!visible || !banner) return null;

  // Pick the Lucide icon by name (falls back to Sparkles)
  const Icon = banner.icon && LucideIcons[banner.icon] ? LucideIcons[banner.icon] : Sparkles;
  // Accent color — the banner CMS field. Defaults to PCC gold.
  // Used for: left border, icon, CTA button background, title color.
  const accent = banner.color || '#D7A072';

  return (
    <div
      // Normal document flow. NO sticky / fixed / absolute. Padding here
      // gives the banner side margins from the screen edge AND a tiny
      // gap above + below it (the `mb-3` separates it from the hero).
      className="px-3 pt-3 mb-3 transition-all duration-300 ease-out"
      style={{
        transform: entered ? 'translateY(0)' : 'translateY(-8px)',
        opacity: entered ? 1 : 0,
      }}
      data-testid="seasonal-banner"
    >
      <div
        // Editorial card: cream background + gold left accent, NO shadow.
        // border-l-4 gives the gold "ribbon" feel without competing with
        // the hero photo below.
        className="rounded-xl px-3.5 py-2.5 flex items-center gap-3 border border-[#EFE4D0]"
        style={{
          background: '#FBF5EA',
          borderLeftWidth: '4px',
          borderLeftColor: accent,
        }}
      >
        {/* Icon — small, accent-tinted, no heavy chip background */}
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${accent}1F` }}  // ~12% opacity
        >
          <Icon size={16} strokeWidth={2} style={{ color: accent }} />
        </div>

        {/* Text — title in accent dark gold, body in warm gray */}
        <div className="flex-1 min-w-0">
          <p
            className="text-sm font-bold leading-tight"
            style={{ color: '#7E5630' }}
          >
            {banner.title}
          </p>
          {banner.message && (
            <p className="text-[12px] leading-snug mt-0.5 text-[#8B7860]">
              {banner.message}
            </p>
          )}
        </div>

        {/* CTA — slightly smaller, gold accent, arrow nudge */}
        {banner.ctaLabel && banner.ctaUrl && (
          <button
            onClick={handleCta}
            className="flex-shrink-0 flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-md text-white transition-colors"
            style={{ background: accent }}
            onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(0.92)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
            data-testid="seasonal-banner-cta"
          >
            {banner.ctaLabel}
            <ArrowRight size={11} strokeWidth={2.5} />
          </button>
        )}

        {/* Close — smaller, lower contrast, doesn't compete with CTA */}
        <button
          onClick={handleDismiss}
          aria-label="Fermer"
          className="flex-shrink-0 p-1 rounded-md text-[#B8A78A] hover:text-[#7E5630] hover:bg-[#EFE4D0] transition-colors"
          data-testid="seasonal-banner-dismiss"
        >
          <X size={14} strokeWidth={2.25} />
        </button>
      </div>
    </div>
  );
};

export default SeasonalBanner;
